// src/components/AppointmentsTab.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getToken } from '../utils/auth';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
  toDatetimeLocalValue,
  fromDatetimeLocalValue,
  defaultDurationMinutes,
  computeEndFromStartLocal,
  appointmentAuthHeaders,
  appointmentTimeBucket,
  APPOINTMENT_TITLE_MAX_LENGTH,
  APPOINTMENT_NOTES_MAX_LENGTH,
  APPOINTMENT_TITLE_CUSTOM,
  APPOINTMENT_TITLE_PRESETS,
  resolveAppointmentTitleMode,
} from '../utils/appointmentHelpers';
import Modal, { DialogHeader, DialogFooter, DialogBody } from './ui/Modal';
import { RefreshCw, Plus } from 'lucide-react';

const APPOINTMENT_SAVE_ERRORS = {
  patient_not_found: 'Selected patient was not found.',
  provider_not_found: 'Clinician was not found.',
  location_not_found: 'Location was not found.',
  scheduled_by_user_not_found: 'Scheduler user was not found.',
  invalid_time_range: 'End time must be after start time.',
  scheduling_conflict: 'That time overlaps another appointment for this clinician. Pick a different slot.',
};

const AppointmentsTab = ({ patientAppointments, pmrn, patientName, API_BASE_URL, onRefresh }) => {
    const { showToast } = useToast();
    const { user, userType } = useAuth();
    const isClinicianUser = String(userType || '').toLowerCase() === 'clinician';
    const isAdminUser = String(userType || '').toLowerCase() === 'admin';
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [editingAppointment, setEditingAppointment] = useState(null);
    const [loading, setLoading] = useState(false);
    const [clinicians, setClinicians] = useState([]);
    const [currentClinician, setCurrentClinician] = useState(null);
    const [listBucket, setListBucket] = useState('upcoming');
    
    const [formData, setFormData] = useState({
        title: 'New Consultation',
        titleMode: 'New Consultation',
        appointmentType: 'In-Person',
        startTime: '',
        endTime: '',
        duration: 30,
        provider_id: '',
        confirmation_notes: '',
    });

    const titleOptions = APPOINTMENT_TITLE_PRESETS;
    const appointmentTypes = ["In-Person", "Online", "Emergency"];

    const lockedProviderId = currentClinician?.id || '';
    const clinicianDisplayName = useMemo(() => {
        const c = currentClinician || clinicians[0];
        if (!c) return '';
        return (
            c.name ||
            `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
            `Clinician ${c.id}`
        );
    }, [currentClinician, clinicians]);

    const filteredPatientAppointments = (patientAppointments || []).filter((appt) => {
        if (listBucket === 'all') return true;
        return appointmentTimeBucket(appt) === listBucket;
    });

    const bucketCounts = (patientAppointments || []).reduce(
        (acc, appt) => {
            const bucket = appointmentTimeBucket(appt);
            acc[bucket] += 1;
            acc.all += 1;
            return acc;
        },
        { upcoming: 0, past: 0, all: 0 }
    );
    const fetchClinicians = useCallback(async () => {
        try {
            const token = getToken();
            if (isClinicianUser && user?.user_id) {
                const response = await axios.get(
                    `${API_BASE_URL}/clinicians/user/${user.user_id}`,
                    { headers: appointmentAuthHeaders(token) }
                );
                const data = response.data;
                setClinicians(data ? [data] : []);
                setCurrentClinician(data || null);
                if (data?.id) {
                    setFormData((prev) => ({
                        ...prev,
                        provider_id: prev.provider_id || data.id,
                    }));
                }
                return;
            }
            if (!isAdminUser) {
                setClinicians([]);
                setCurrentClinician(null);
                return;
            }
            const response = await axios.get(`${API_BASE_URL}/clinicians/`, {
                headers: appointmentAuthHeaders(token),
            });
            setClinicians(Array.isArray(response.data) ? response.data : []);
            setCurrentClinician(null);
        } catch (error) {
            console.error('Error fetching clinicians:', error);
        }
    }, [API_BASE_URL, isAdminUser, isClinicianUser, user?.user_id]);

    useEffect(() => {
        fetchClinicians();
    }, [fetchClinicians]);

    useEffect(() => {
        if (!showAppointmentModal) return undefined;
        const onEsc = (e) => {
            if (e.key === 'Escape' && !loading) {
                setFormData({
                    title: 'New Consultation',
                    appointmentType: 'In-Person',
                    startTime: '',
                    endTime: '',
                    duration: 30,
                    provider_id: ''
                });
                setEditingAppointment(null);
                setShowAppointmentModal(false);
            }
        };
        window.addEventListener('keydown', onEsc);
        return () => window.removeEventListener('keydown', onEsc);
    }, [showAppointmentModal, loading]);

    const formatDate = (isoString) => {
        if (!isoString) return 'N/A';
        return isoString.split('T')[0];
    };

    const formatTime = (isoString) => {
        if (!isoString) return 'N/A';
        const timePart = isoString.split('T')[1];
        return timePart ? timePart.substring(0, 5) : 'N/A';
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            const next = { ...prev };
            if (name === 'provider_id') {
                next.provider_id = parseInt(value, 10) || '';
            } else if (name === 'titleMode') {
                next.titleMode = value;
                if (value === APPOINTMENT_TITLE_CUSTOM) {
                    if (APPOINTMENT_TITLE_PRESETS.includes(next.title)) {
                        next.title = '';
                    }
                } else {
                    next.title = value;
                }
            } else if (name === 'title') {
                next.title = String(value).slice(0, APPOINTMENT_TITLE_MAX_LENGTH);
                next.titleMode = APPOINTMENT_TITLE_CUSTOM;
            } else if (name === 'confirmation_notes') {
                next.confirmation_notes = String(value).slice(0, APPOINTMENT_NOTES_MAX_LENGTH);
            } else if (name === 'duration') {
                const mins = Math.max(5, parseInt(value, 10) || 30);
                next.duration = mins;
                if (next.startTime) {
                    next.endTime = computeEndFromStartLocal(next.startTime, mins);
                }
            } else if (name === 'startTime') {
                next.startTime = value;
                next.endTime = computeEndFromStartLocal(value, Number(next.duration) || 30);
            } else if (name === 'endTime') {
                next.endTime = value;
                if (next.startTime && value) {
                    const startMs = new Date(next.startTime).getTime();
                    const endMs = new Date(value).getTime();
                    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
                        next.duration = Math.max(5, Math.round((endMs - startMs) / 60000));
                    }
                }
            } else {
                next[name] = value;
            }
            return next;
        });
    };

    const resetForm = () => {
        setFormData({
            title: 'New Consultation',
            titleMode: 'New Consultation',
            appointmentType: 'In-Person',
            startTime: '',
            endTime: '',
            duration: 30,
            provider_id: isClinicianUser ? (lockedProviderId || '') : '',
            confirmation_notes: '',
        });
        setEditingAppointment(null);
        setShowAppointmentModal(false);
    };

    const openCreateModal = () => {
        setEditingAppointment(null);
        setFormData({
            title: 'New Consultation',
            titleMode: 'New Consultation',
            appointmentType: 'In-Person',
            startTime: '',
            endTime: '',
            duration: 30,
            provider_id: isClinicianUser ? (lockedProviderId || '') : '',
            confirmation_notes: '',
        });
        setShowAppointmentModal(true);
    };

    const buildUrl = (baseUrl, path, hasId = false) => {
        const cleanBase = baseUrl.replace(/\/+$/, '');
        const cleanPath = path.replace(/^\/+|\/+$/g, '');
        return hasId ? `${cleanBase}/${cleanPath}` : `${cleanBase}/${cleanPath}/`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const providerId = isClinicianUser ? lockedProviderId : formData.provider_id;
        const titleValue = String(formData.title || '').trim();
        if (!titleValue || !formData.startTime || !providerId) {
            showToast('Please fill all required fields', 'error');
            return;
        }
        if (titleValue.length > APPOINTMENT_TITLE_MAX_LENGTH) {
            showToast(`Title must be ${APPOINTMENT_TITLE_MAX_LENGTH} characters or fewer`, 'error');
            return;
        }

        const startIso = fromDatetimeLocalValue(formData.startTime);
        const endIso =
            fromDatetimeLocalValue(formData.endTime) ||
            fromDatetimeLocalValue(
                computeEndFromStartLocal(formData.startTime, formData.duration || 30)
            );
        if (!startIso || !endIso) {
            showToast('Invalid start or end time', 'error');
            return;
        }
        if (new Date(endIso) <= new Date(startIso)) {
            showToast('End time must be after start time', 'error');
            return;
        }

        try {
            setLoading(true);
            const token = getToken();
            if (!token) {
                showToast('Not signed in', 'error');
                return;
            }

            const isEditing = !!editingAppointment;
            const url = isEditing
                ? buildUrl(API_BASE_URL, `appointments/${editingAppointment.id}`, true)
                : buildUrl(API_BASE_URL, 'appointments', false);

            const method = isEditing ? 'PUT' : 'POST';

            const notesValue = String(formData.confirmation_notes || '').trim();
            if (notesValue.length > APPOINTMENT_NOTES_MAX_LENGTH) {
                showToast(`Notes must be ${APPOINTMENT_NOTES_MAX_LENGTH} characters or fewer`, 'error');
                return;
            }

            const submitData = {
                title: titleValue,
                appointmentType: formData.appointmentType,
                startTime: startIso,
                endTime: endIso,
                duration: Number(formData.duration) || 30,
                provider_id: parseInt(providerId, 10),
                PMRN: parseInt(pmrn, 10),
                status: isEditing ? (editingAppointment.status || 'Confirmed') : 'Confirmed',
                confirmation_notes: notesValue || null,
            };

            const response = await axios({
                method,
                url,
                data: submitData,
                headers: appointmentAuthHeaders(token),
            });

            if (response.status === 200 || response.status === 201) {
                showToast(isEditing ? 'Appointment updated successfully' : 'Appointment created successfully', 'success');
                resetForm();
                if (onRefresh) {
                    await onRefresh();
                }
            }
        } catch (error) {
            console.error('Error saving appointment:', error);
            const detail = error.response?.data?.detail;
            const code = typeof detail === 'string' ? detail : null;
            const errorMsg =
                (code && APPOINTMENT_SAVE_ERRORS[code]) ||
                (typeof detail === 'string' ? detail : null) ||
                error.message ||
                'Unknown error';
            showToast(`Failed to save appointment: ${errorMsg}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (appointment) => {
        const duration = defaultDurationMinutes(appointment);
        const startLocal = toDatetimeLocalValue(appointment.startTime);
        const endLocal = appointment.endTime
            ? toDatetimeLocalValue(appointment.endTime)
            : computeEndFromStartLocal(startLocal, duration);
        setEditingAppointment(appointment);
        const titleResolved = resolveAppointmentTitleMode(appointment.title || 'New Consultation');
        setFormData({
            title: titleResolved.mode === APPOINTMENT_TITLE_CUSTOM
                ? titleResolved.customTitle
                : titleResolved.mode,
            titleMode: titleResolved.mode,
            appointmentType: appointment.appointmentType || 'In-Person',
            startTime: startLocal,
            endTime: endLocal,
            duration,
            provider_id: isClinicianUser
                ? (lockedProviderId || appointment.provider_id || '')
                : (appointment.provider_id || ''),
            confirmation_notes: appointment.confirmation_notes || '',
        });
        setShowAppointmentModal(true);
    };

    const handleDelete = async (appointmentId) => {
        if (!appointmentId) {
            showToast('Invalid appointment ID', 'error');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this appointment?')) return;

        try {
            setLoading(true);
            const token = getToken();
            if (!token) {
                showToast('Not signed in', 'error');
                return;
            }
            const url = buildUrl(API_BASE_URL, `appointments/${appointmentId}`, true);
            const response = await axios.delete(url, {
                headers: appointmentAuthHeaders(token),
            });

            if (response.status === 200 || response.status === 204) {
                showToast('Appointment deleted successfully', 'success');
                if (onRefresh) {
                    await onRefresh();
                }
            } else {
                throw new Error(`Unexpected response status: ${response.status}`);
            }
        } catch (error) {
            console.error('Error deleting appointment:', error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
            showToast(`Failed to delete appointment: ${errorMsg}`, 'error');
            if (onRefresh) await onRefresh();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-md shadow-sm border">
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-700">Appointments</h3>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={async () => {
                                if (onRefresh) {
                                    setLoading(true);
                                    try {
                                        await onRefresh();
                                        showToast('Appointments refreshed', 'success');
                                    } catch (error) {
                                        showToast('Failed to refresh appointments', 'error');
                                    } finally {
                                        setLoading(false);
                                    }
                                }
                            }}
                            className="border border-gray-300 text-gray-600 px-3 py-2 rounded hover:bg-gray-50 text-sm flex items-center space-x-2"
                            disabled={loading}
                            title="Refresh appointments"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={2} aria-hidden="true" />
                            {loading && <span>Refreshing...</span>}
                        </button>
                        <button
                            onClick={openCreateModal}
                            className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-500 text-sm flex items-center space-x-2"
                            disabled={loading}
                        >
                            <Plus className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
                            <span>Add New Appointment</span>
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Appointment time filter">
                    {[
                        { id: 'upcoming', label: 'Upcoming' },
                        { id: 'past', label: 'Past' },
                        { id: 'all', label: 'All' },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={listBucket === tab.id}
                            onClick={() => setListBucket(tab.id)}
                            className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                                listBucket === tab.id
                                    ? 'bg-teal-600 text-white border-teal-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {tab.label}
                            <span className={`ml-1.5 ${listBucket === tab.id ? 'text-teal-100' : 'text-gray-500'}`}>
                                ({bucketCounts[tab.id] || 0})
                            </span>
                        </button>
                    ))}
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Patient Name</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Time</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Type</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Clinician</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredPatientAppointments.length > 0 ? (
                                filteredPatientAppointments.map((appointment, index) => (
                                    <tr key={appointment.id || index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            {appointment.patient_name || patientName}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                appointment.status === 'Confirmed' ? 'bg-green-100 text-green-800' :
                                                appointment.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                                appointment.status === 'Completed' ? 'bg-slate-100 text-slate-700' :
                                                appointment.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {appointment.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {formatDate(appointment.startTime)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {formatTime(appointment.startTime)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {appointment.appointmentType}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {appointment.provider_name}
                                        </td>
                                        <td className="px-4 py-3 text-sm space-x-2">
                                            <button
                                                onClick={() => handleEdit(appointment)}
                                                className="text-blue-600 hover:text-blue-800 text-sm"
                                                disabled={loading}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(appointment.id)}
                                                className="text-red-600 hover:text-red-800 text-sm"
                                                disabled={loading}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="text-center py-4 text-gray-500">
                                        {listBucket === 'upcoming'
                                            ? 'No upcoming appointments for this patient.'
                                            : listBucket === 'past'
                                              ? 'No past appointments for this patient.'
                                              : 'No appointments found for this patient.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Appointment Modal */}
            <Modal
                open={showAppointmentModal}
                onClose={() => { if (!loading) resetForm(); }}
            >
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-2xl w-full mx-4 max-h-[min(90vh,840px)] overflow-hidden flex flex-col">
                        <DialogHeader
                            title={editingAppointment ? 'Edit Appointment' : 'Add New Appointment'}
                            subtitle={editingAppointment ? 'Update schedule and visit details.' : 'Schedule a visit for this patient.'}
                            onClose={() => { if (!loading) resetForm(); }}
                            tone="muted"
                            className={loading ? '[&_button]:opacity-50 [&_button]:pointer-events-none' : ''}
                        />

                        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
                            <DialogBody className="px-6 py-4 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Patient
                                    </label>
                                    <input
                                        type="text"
                                        value={patientName}
                                        disabled
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Title <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="titleMode"
                                        value={formData.titleMode}
                                        onChange={handleFormChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                                        required
                                        disabled={loading}
                                    >
                                        {titleOptions.map((title) => (
                                            <option key={title} value={title}>{title}</option>
                                        ))}
                                        <option value={APPOINTMENT_TITLE_CUSTOM}>Custom title…</option>
                                    </select>
                                    {formData.titleMode === APPOINTMENT_TITLE_CUSTOM && (
                                        <div className="mt-2">
                                            <input
                                                type="text"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleFormChange}
                                                maxLength={APPOINTMENT_TITLE_MAX_LENGTH}
                                                placeholder="Enter appointment title"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                                                required
                                                disabled={loading}
                                            />
                                            <p className="mt-1 text-xs text-gray-500 text-right">
                                                {String(formData.title || '').length}/{APPOINTMENT_TITLE_MAX_LENGTH}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Appointment Type <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="appointmentType"
                                        value={formData.appointmentType}
                                        onChange={handleFormChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                                        required
                                        disabled={loading}
                                    >
                                        {appointmentTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Start <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="startTime"
                                        value={formData.startTime}
                                        onChange={handleFormChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                                        required
                                        disabled={loading}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Duration (minutes)
                                    </label>
                                    <select
                                        name="duration"
                                        value={formData.duration}
                                        onChange={handleFormChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                                        disabled={loading}
                                    >
                                        {[15, 20, 30, 45, 60, 90, 120].map((m) => (
                                            <option key={m} value={m}>{m} min</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        End <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="endTime"
                                        value={formData.endTime}
                                        onChange={handleFormChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                                        required
                                        disabled={loading}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Clinician <span className="text-red-500">*</span>
                                    </label>
                                    {isClinicianUser ? (
                                        <input
                                            type="text"
                                            value={clinicianDisplayName || 'Loading…'}
                                            readOnly
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-800"
                                            disabled={loading}
                                        />
                                    ) : (
                                        <select
                                            name="provider_id"
                                            value={formData.provider_id}
                                            onChange={handleFormChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                                            required
                                            disabled={loading}
                                        >
                                            <option value="">Select Clinician</option>
                                            {clinicians.map((clinician) => {
                                                const fullName =
                                                    clinician.name ||
                                                    `${clinician.firstName || ''} ${clinician.lastName || ''}`.trim() ||
                                                    `Clinician ${clinician.id}`;
                                                const roleLabel = clinician.role || clinician.speciality || 'Care Team';
                                                return (
                                                    <option key={clinician.id} value={clinician.id}>
                                                        {fullName} — {roleLabel}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Notes
                                    </label>
                                    <textarea
                                        name="confirmation_notes"
                                        rows={3}
                                        value={formData.confirmation_notes}
                                        onChange={handleFormChange}
                                        maxLength={APPOINTMENT_NOTES_MAX_LENGTH}
                                        placeholder="Optional notes for this appointment"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 resize-y min-h-[88px]"
                                        disabled={loading}
                                    />
                                    <p className="mt-1 text-xs text-gray-500 text-right">
                                        {String(formData.confirmation_notes || '').length}/{APPOINTMENT_NOTES_MAX_LENGTH}
                                    </p>
                                </div>
                            </DialogBody>

                            <DialogFooter>
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                                        disabled={loading}
                                    >
                                        {loading ? 'Saving...' : (editingAppointment ? 'Update Appointment' : 'Create Appointment')}
                                    </button>
                            </DialogFooter>
                        </form>
                    </div>
            </Modal>
        </div>
    );
};

export default AppointmentsTab;